import * as React from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  Divider,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from '@/components/core/v2/toaster';

import { updateResidentCustomFieldValues } from '@/api/residents';
import { PropertyItem } from '@/components/core/v2/property-item';
import { PropertyList } from '@/components/core/v2/property-list';

export interface CustomFieldDef {
  id: string;
  name: string;
  key: string;
  visible_to_member: boolean;
  editable_by_member: boolean;
  sort_order: number;
}

interface ResidentCustomFieldsProps {
  communityId: string;
  residentId: string;
  customFields: CustomFieldDef[];
  customFieldValues: Record<string, string>;
  isManager: boolean;
  isViewingSelf: boolean;
  queryKey: (string | undefined)[];
}

export function ResidentCustomFields({
  communityId,
  residentId,
  customFields,
  customFieldValues,
  isManager,
  isViewingSelf,
  queryKey,
}: ResidentCustomFieldsProps): React.JSX.Element | null {
  const queryClient = useQueryClient();
  const [values, setValues] = React.useState<Record<string, string>>(() => ({ ...customFieldValues }));

  React.useEffect(() => {
    setValues((prev) => ({ ...customFieldValues, ...prev }));
  }, [customFieldValues]);

  const mutation = useMutation({
    mutationFn: (payload: Record<string, string | null>) =>
      updateResidentCustomFieldValues(communityId, residentId, payload),
    onSuccess: (res, variables) => {
      setValues((prev) => ({ ...prev, ...res.data }));
      queryClient.invalidateQueries({ queryKey });
      const names = customFields.filter((f) => f.id in (variables ?? {})).map((f) => f.name);
      const suffix = names.length > 0 ? `: ${names.join(', ')}` : '';
      toast.success(`Saved${suffix}`);
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message ?? 'Failed to save');
    },
  });

  const handleSave = () => {
    mutation.mutate(values);
  };

  if (!customFields || customFields.length === 0) {
    return null;
  }

  const hasEditable = customFields.some(
    (f) => isManager || (isViewingSelf && f.editable_by_member)
  );

  return (
    <Card>
      <CardHeader
        title="Additional details"
        subheader="Custom fields for this resident (e.g. emergency contact, parking spot)."
        subheaderTypographyProps={{ color: 'text.secondary' }}
        action={
          hasEditable ? (
            <LoadingButton
              size="small"
              variant="contained"
              loading={mutation.isPending}
              onClick={handleSave}
            >
              Save
            </LoadingButton>
          ) : null
        }
      />
      <CardContent sx={{ pt: 0 }}>
        <PropertyList divider={<Divider />} orientation="vertical" sx={{ '--PropertyItem-padding': '12px 24px' }}>
          {customFields.map((field) => {
            const editable = isManager || (isViewingSelf && field.editable_by_member);
            const value = values[field.id] ?? '';

            return (
              <PropertyItem
                key={field.id}
                name={field.name}
                value={
                  editable ? (
                    <TextField
                      size="small"
                      fullWidth
                      placeholder={`Enter ${field.name}`}
                      value={value}
                      onChange={(e) =>
                        setValues((prev) => ({ ...prev, [field.id]: e.target.value }))
                      }
                      sx={{ maxWidth: 360 }}
                    />
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      {value || '—'}
                    </Typography>
                  )
                }
              />
            );
          })}
        </PropertyList>
      </CardContent>
    </Card>
  );
}
